import { useMemo, useState } from "react";
import { Badge, Button, Label, Select, TextInput, Textarea } from "flowbite-react";
import {
  HiChevronDown,
  HiChevronUp,
  HiLockClosed,
  HiOutlineCloudUpload,
  HiOutlinePencil,
  HiOutlineQuestionMarkCircle,
  HiX,
} from "react-icons/hi";
import { FaSearch, FaWhatsapp } from "react-icons/fa";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { useSelector } from "react-redux";

const SUPORTE_WHATSAPP = "5564999748520";

const CONTEXT_OPTIONS = [
  { value: "Dashboard e Metas", label: "Dashboard e Metas" },
  { value: "Perfil", label: "Perfil" },
  { value: "Loja", label: "Loja" },
  { value: "Produtos", label: "Produtos" },
  { value: "Fotos", label: "Fotos" },
  { value: "Tags", label: "Tags" },
  { value: "Gerar Link", label: "Gerar Link" },
  { value: "Plano", label: "Plano" },
  { value: "Ajuda", label: "Ajuda" },
];

const FaqItem = ({ question, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between text-left px-4 py-3"
      >
        <span className="font-semibold text-gray-800">{question}</span>
        {open ? (
          <HiChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <HiChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>
      {open ? (
        <div className="px-4 pb-4 text-sm text-gray-700 space-y-3">{children}</div>
      ) : null}
    </div>
  );
};

const MockProdutoModal = () => (
  <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
    <div className="bg-white border-b px-4 py-3 flex items-center gap-2">
      <HiOutlineCloudUpload className="w-5 h-5 text-gray-700" />
      <span className="font-medium text-gray-800">Cadastrar Produto</span>
    </div>
    <div className="p-4 space-y-3">
      <div>
        <Label htmlFor="help-ref">Referencia *</Label>
        <TextInput id="help-ref" value="18337" disabled />
      </div>
      <div>
        <Label htmlFor="help-desc">Descricao *</Label>
        <TextInput id="help-desc" value="CALCA 18337 443 VERDE MILITAR CIA MARITIMA" disabled />
      </div>
      <div>
        <Label htmlFor="help-brand">Marca *</Label>
        <TextInput id="help-brand" value="Cia Maritima" disabled />
      </div>
      <div className="text-xs text-gray-500">
        Campos obrigatorios para criar produto: Referencia, Descricao e Marca.
      </div>
      <div className="flex gap-2">
        <Button size="xs" color="blue" disabled>
          <HiOutlinePencil className="w-4 h-4 mr-1" />
          Salvar
        </Button>
        <Button size="xs" color="gray" disabled>
          Cancelar
        </Button>
      </div>
    </div>
  </div>
);

const MockUploadFotos = () => (
  <div className="border border-gray-200 rounded-xl bg-white p-4 space-y-3">
    <h4 className="font-medium text-gray-800">Secao de Fotos do Produto</h4>
    <TextInput
      type="file"
      disabled
      helperText="Disponivel somente apos o produto existir."
    />
    <div className="grid grid-cols-3 gap-2">
      <div className="h-20 rounded bg-gray-100 border" />
      <div className="h-20 rounded bg-gray-100 border" />
      <div className="h-20 rounded bg-gray-100 border" />
    </div>
    <p className="text-xs text-gray-500">Carrossel com fotos ja cadastradas do produto.</p>
  </div>
);

const MockTagFlow = () => {
  const [inputValue, setInputValue] = useState("ver");
  const [selectedTags, setSelectedTags] = useState(["praia"]);
  const [allTags, setAllTags] = useState([
    "verao",
    "Verao26",
    "danca",
    "fitness",
    "infantil",
    "masculino",
    "natacao",
  ]);

  const lowerInput = inputValue.trim().toLowerCase();

  const sugestoes = useMemo(() => {
    if (!lowerInput) return [];
    return allTags
      .filter((tag) => tag.toLowerCase().includes(lowerInput))
      .filter((tag) => !selectedTags.includes(tag))
      .slice(0, 5);
  }, [allTags, lowerInput, selectedTags]);

  const exactMatch = allTags.some((tag) => tag.toLowerCase() === lowerInput);
  const podeCriar = lowerInput.length > 0 && !exactMatch;

  const frequentes = useMemo(
    () => allTags.filter((tag) => !selectedTags.includes(tag)).slice(2, 7),
    [allTags, selectedTags]
  );

  const addTag = (tag) => {
    if (!selectedTags.includes(tag)) {
      setSelectedTags((prev) => [...prev, tag]);
    }
    setInputValue("");
  };

  const criarTag = () => {
    const nova = inputValue.trim();
    if (!nova) return;
    if (!allTags.includes(nova)) {
      setAllTags((prev) => [nova, ...prev]);
    }
    addTag(nova);
  };

  const removeTag = (tag) => {
    setSelectedTags((prev) => prev.filter((item) => item !== tag));
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white p-4 space-y-2">
      <Label htmlFor="help-tag-input" className="text-base font-semibold">
        Tags
      </Label>
      <TextInput
        id="help-tag-input"
        icon={FaSearch}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder="Busque uma tag ou crie uma nova."
      />
      <p className="text-xs text-purple-500">
        Voce pode adicionar varias - Ex: Feminino, Verao, Promocao.
      </p>

      {lowerInput ? (
        <ul className="bg-white border border-gray-300 rounded shadow-sm max-h-40 overflow-auto">
          {sugestoes.map((tag) => (
            <li
              key={tag}
              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => addTag(tag)}
            >
              <span className="text-gray-400 text-xs"> + </span>
              <span className="text-gray-700">{tag}</span>
            </li>
          ))}
          {podeCriar ? (
            <li
              className="px-3 py-2 flex items-center gap-2 cursor-pointer bg-purple-200 hover:bg-purple-300 border-t"
              onClick={criarTag}
            >
              <span className="text-purple-700 font-medium">+ Criar tag</span>
              <span className="text-purple-700">"{inputValue.trim()}"</span>
            </li>
          ) : null}
        </ul>
      ) : null}

      <div className="pt-1">
        <p className="text-sm text-gray-500 mb-2">Mais frequentes:</p>
        <div className="flex flex-wrap gap-2">
          {frequentes.map((tag) => (
            <button
              type="button"
              key={tag}
              className="px-3 py-1 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100"
              onClick={() => addTag(tag)}
            >
              + {tag}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        {selectedTags.map((tag) => (
          <Badge
            key={tag}
            color="purple"
            className="inline-flex items-center px-2 py-1 text-sm"
          >
            {tag}
            <HiX
              className="ml-1 w-4 h-4 opacity-70 hover:opacity-100 cursor-pointer"
              onClick={() => removeTag(tag)}
            />
          </Badge>
        ))}
      </div>
    </div>
  );
};

const MockCardCatalogoInterativo = () => {
  const [valor, setValor] = useState(false);
  const [desconto, setDesconto] = useState(true);
  const [tamanhos, setTamanhos] = useState(false);
  const [parcelas, setParcelas] = useState(false);

  const valorProduto = valor ? 499 : null;
  const descontoProduto = desconto ? 15 : null;
  const maximoParcelas = parcelas ? 6 : null;
  const tamanhosDisponiveis = tamanhos ? "M, G, GG" : "";

  const formatMoeda = (number) =>
    Number(number).toFixed(2).replace(".", ",");

  const valorParcela =
    valorProduto !== null && maximoParcelas
      ? formatMoeda(valorProduto / maximoParcelas)
      : null;

  const valorComDesconto =
    valorProduto !== null && descontoProduto !== null
      ? formatMoeda(valorProduto * (1 - descontoProduto / 100))
      : null;

  return (
    <div className="mt-4 flex justify-center">
      <div className="flex flex-row flex-wrap sm:flex-nowrap items-center sm:items-stretch bg-white rounded-3xl shadow-xl p-4 sm:p-6 gap-6 w-full max-w-5xl">
        <div className="flex flex-col justify-center gap-4 w-full sm:w-auto min-w-[180px]">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={valor}
              onChange={(e) => setValor(e.target.checked)}
              className="sr-only peer"
            />
            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
            <span className="ms-3 text-sm font-medium text-gray-900">Valor</span>
          </label>

          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={desconto}
              onChange={(e) => setDesconto(e.target.checked)}
              className="sr-only peer"
            />
            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
            <span className="ms-3 text-sm font-medium text-gray-900">Desconto</span>
          </label>

          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={tamanhos}
              onChange={(e) => setTamanhos(e.target.checked)}
              className="sr-only peer"
            />
            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
            <span className="ms-3 text-sm font-medium text-gray-900">Tamanhos</span>
          </label>

          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={parcelas}
              onChange={(e) => setParcelas(e.target.checked)}
              className="sr-only peer"
            />
            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
            <span className="ms-3 text-sm font-medium text-gray-900">Parcelas</span>
          </label>
        </div>

        <div className="w-full flex justify-center">
          <div className="w-full max-w-md bg-white rounded-3xl border border-gray-200 p-4 space-y-3">
            <div className="flex gap-2">
              <div className="w-16 h-24 rounded-lg bg-[#ead9c8]" />
              <div className="w-16 h-24 rounded-lg bg-[#ead9c8]" />
            </div>

            {(true || descontoProduto !== null) && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-gray-200 text-gray-700 font-medium">
                  ZAYA
                </span>
                {descontoProduto !== null ? (
                  <span className="px-3 py-1 rounded-full bg-black text-white font-semibold">
                    {descontoProduto}% OFF
                  </span>
                ) : null}
              </div>
            )}

            {valorProduto !== null || maximoParcelas !== null ? (
              <div className="space-y-1">
                {valorProduto !== null && maximoParcelas ? (
                  <>
                    <span className="inline-block bg-green-100 text-green-800 text-base font-semibold px-3 py-1 rounded-full">
                      {maximoParcelas}x de R$ {valorParcela}
                    </span>

                    {descontoProduto !== null ? (
                      <div className="text-xs text-gray-500">
                        ou 1 X de{" "}
                        <span className="font-semibold text-gray-700">
                          R$ {valorComDesconto}
                        </span>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">
                        a vista{" "}
                        <span className="font-semibold text-gray-700">
                          R$ {formatMoeda(valorProduto)}
                        </span>
                      </div>
                    )}
                  </>
                ) : valorProduto !== null ? (
                  <p className="text-4xl font-bold text-gray-900 leading-tight">
                    R$ {formatMoeda(valorProduto)}
                  </p>
                ) : (
                  <span className="inline-block bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full">
                    Dividimos em ate {maximoParcelas}x
                  </span>
                )}
              </div>
            ) : null}

            {tamanhosDisponiveis && tamanhosDisponiveis.trim() !== "" ? (
              <div>
                <p className="text-sm text-gray-500">Tamanhos disponiveis:</p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {tamanhosDisponiveis.split(",").map((size, idx) => (
                    <span
                      key={`${size}-${idx}`}
                      className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full"
                    >
                      {size.trim()}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="border-t border-gray-200 pt-3 space-y-3">
              <button className="w-full bg-green-700 text-white rounded-full py-3 font-semibold flex items-center justify-center gap-2">
                <FaWhatsapp />
                Conversar no WhatsApp
              </button>
              <button className="w-full border border-gray-300 text-gray-800 rounded-full py-3 font-semibold flex items-center justify-center gap-2">
                <HiOutlineLocationMarker />
                Visitar Loja
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Ajuda = () => {
  const { loja, profile } = useSelector((state) => state.user);
  const [contexto, setContexto] = useState("Dashboard e Metas");
  const [duvidaTexto, setDuvidaTexto] = useState("");
  const acessoSuporte = profile?.permissoesPlano?.acessoSuporte !== false;

  const nomeLoja = (loja?.nome_fantasia || "Minha Loja").trim();
  const duvidaNormalizada = duvidaTexto.trim();
  const mensagemSuporte = `[${nomeLoja}][${contexto}]: ${duvidaNormalizada}`;
  const whatsappHref = `https://wa.me/${SUPORTE_WHATSAPP}?text=${encodeURIComponent(
    mensagemSuporte
  )}`;
  const podeEnviarDuvida = duvidaNormalizada.length >= 8;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mt-8 lg:mt-2">
        <HiOutlineQuestionMarkCircle className="w-7 h-7 text-gray-700" />
        <h2 className="text-xl md:text-2xl font-semibold text-gray-700 select-none cursor-default">
          Ajuda e FAQ
        </h2>
      </div>

      <p className="text-sm text-gray-600 max-w-4xl">
        Guia rapido com fluxos reais do Vintree: cadastro de produtos, fotos e tags.
      </p>

      <div className="space-y-3">
        <FaqItem question="Como cadastrar meu primeiro produto?" defaultOpen>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Acesse /app/produtos e clique no botao +.</li>
            <li>Preencha os campos obrigatorios: Referencia, Descricao e Marca.</li>
            <li>Opcional: adicione tags e detalhes (tamanho, cor, valor, desconto e parcelas).</li>
            <li>Clique em Salvar para criar o produto.</li>
          </ol>
          <MockProdutoModal />
        </FaqItem>

        <FaqItem question="Como cadastrar fotos do produto do jeito certo?">
          <ol className="list-decimal pl-5 space-y-1">
            <li>Primeiro salve o produto.</li>
            <li>No mesmo modal, use a secao de fotos para enviar os arquivos.</li>
            <li>No /app/fotos, revise, pesquise e remova fotos quando necessario.</li>
          </ol>
          <MockUploadFotos />
        </FaqItem>

        <FaqItem question="Tags: o que sao e como cadastrar/relacionar no produto?">
          <p>
            Tags sao categorias flexiveis do catalogo. Um produto pode ter varias tags ao mesmo tempo
            (ex.: vestido, verao, promocao).
          </p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Digite no campo Tags para pesquisar as existentes.</li>
            <li>Para relacionar existente: clique em uma sugestao da lista.</li>
            <li>Para criar nova: clique em "Criar tag "nome-da-tag"".</li>
            <li>A tag criada ja entra relacionada ao produto automaticamente.</li>
            <li>Com campo vazio, use "Mais frequentes" para vincular tags populares com 1 clique.</li>
          </ol>
          <MockTagFlow />
        </FaqItem>

        <FaqItem question="Campos opcionais e comportamento do card no catalogo">
          <ol className="list-decimal pl-5 space-y-1">
            <li>Obrigatorios no cadastro: Referencia, Descricao e Marca.</li>
            <li>Opcionais: valor, desconto, tamanhos, maximo de parcelas, cor/estampa e tags.</li>
            <li>No catalogo, so aparece o que for preenchido no produto.</li>
            <li>A simulacao abaixo replica a logica atual do modal publico do catalogo.</li>
          </ol>
          <MockCardCatalogoInterativo />
        </FaqItem>
      </div>

      <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          {acessoSuporte ? (
            <FaWhatsapp className="text-emerald-700" />
          ) : (
            <HiLockClosed className="text-emerald-700" />
          )}
          <h3 className="text-base font-semibold text-emerald-900">Falar com suporte</h3>
        </div>

        {!acessoSuporte ? (
          <div className="rounded-lg border border-emerald-200 bg-white p-3">
            <p className="text-sm font-semibold text-emerald-900">
              Faca upgrade do seu plano para ter acesso a esse recurso
            </p>
            <p className="text-sm text-emerald-800 mt-1">
              O FAQ continua liberado. O envio direto para o suporte pelo WhatsApp esta
              disponivel nos planos pagos.
            </p>
            <Button
              size="sm"
              color="success"
              className="mt-3"
              onClick={() => {
                window.location.href = "/app/planos";
              }}
            >
              Ver planos
            </Button>
          </div>
        ) : null}

        <p className="text-sm text-emerald-800">
          Descreva sua duvida para enviar uma mensagem ja formatada no WhatsApp.
        </p>

        <div>
          <Label htmlFor="ajuda-contexto" className="mb-2 block">
            Tela/assunto
          </Label>
          <Select
            id="ajuda-contexto"
            value={contexto}
            onChange={(e) => setContexto(e.target.value)}
            disabled={!acessoSuporte}
          >
            {CONTEXT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label htmlFor="ajuda-duvida" className="mb-2 block">
            Sua duvida
          </Label>
          <Textarea
            id="ajuda-duvida"
            rows={4}
            placeholder="Ex.: Estou com duvida sobre a cobranca do plano trimestral..."
            value={duvidaTexto}
            onChange={(e) => setDuvidaTexto(e.target.value)}
            disabled={!acessoSuporte}
          />
        </div>

        <p className="text-xs text-emerald-900">
          Preview: <strong>{mensagemSuporte}</strong>
        </p>

        <Button
          color="success"
          type="button"
          onClick={() => {
            if (!podeEnviarDuvida) return;
            window.open(whatsappHref, "_blank", "noopener,noreferrer");
          }}
          disabled={!acessoSuporte || !podeEnviarDuvida}
        >
          <FaWhatsapp className="mr-2" />
          Enviar no WhatsApp
        </Button>
      </div>
    </div>
  );
};

export default Ajuda;
